import {
  timestampFromDate,
  type_erased,
} from '@psl/functions/api-model-utils/apiModelsConversionUtils';
import {
  DimsInfo as ApiDimsInfo,
  ShippingSurchargeInfo as ApiShippingSurchargeInfo,
  DimsInfoUnitEnum,
  MyStoreListingItem,
  MyStoreListingItemStatusEnum,
  PoundWithOunceWeightInfo,
  PoundWithOunceWeightInfoTypeEnum,
  ProductItem,
  ProductItemStatusEnum,
  ProductsListing,
  ProductsListingStatusEnum,
  ShippingSurchargeInfoRuleEnum,
  SingleUnitWeightInfo,
  SingleUnitWeightInfoTypeEnum,
  SingleUnitWeightInfoUnitEnum,
  StoreListingOriginalInfo,
  StoreListingOriginalInfoTypeEnum,
  StoreListingVariant,
  StoreListingVariantOriginalInfoTypeEnum,
} from '../../generated/api';
import {
  DimsInfo,
  DistanceUnit,
  Listing,
  ListingOriginType,
  ListingStatus,
  ListingVariant,
  MassUnit,
  ShippingSurchargeInfo,
  ShippingSurchargeRule,
  WeightInfo,
} from '../../models/ClientModels';
import { apiServerTimeToAppDate } from '../api-model-utils/apiServerTimeToAppDate';
import { WithID } from '../firebase/rxfireExtensions';

export interface StoreListingRequest {
  name: string;
  price: number;
  quantity: number;
  defaultImage: string;
  description?: string;
  discountPrice?: number;
  sku?: string;
  extraImages?: Array<string>;
  variants?: Array<StoreListingVariant>;
  maximumPurchaseQuantity?: number;
  maximumPurchaseQuantityPerLiveStream?: number;
  shippingFee?: number;
  tags?: Array<string>;
  originalInfo?: StoreListingOriginalInfo;
}

export function storeListingToApiServerStoreListingRequest(
  listing: Listing,
): StoreListingRequest {
  return {
    ...listing,
    maximumPurchaseQuantity: listing.maximumPurchaseQuantity ?? undefined,
    shippingFee: listing.shippingFee ?? undefined,
    variants: listing.variants?.map(
      listingVariantToApiServerStoreListingVariantRequest,
    ),
    originalInfo:
      listing.originalInfo == null
        ? undefined
        : {
            ...listing.originalInfo,
            type: listingOriginTypeToApiServerStoreListingOriginalInfoTypeEnum(
              listing.originalInfo.type,
            ),
          },
  };
}

export function listingVariantToApiServerStoreListingVariantRequest(
  v: ListingVariant,
): StoreListingVariant {
  return {
    ...v,
    dims: v.dims == null ? undefined : listDimsToApiServerListDims(v.dims),
    weight:
      v.weight == null ? undefined : listWeightToApiServerListWeight(v.weight),
    originalInfo:
      v.originalInfo == null
        ? undefined
        : {
            ...v.originalInfo,
            type: listingOriginTypeToApiServerStoreListingVariantOriginalInfoTypeEnum(
              v.originalInfo.type,
            ),
          },
  };
}

export function listShippingSurchargeRuleToApiServerListShippingSurchargeRule(
  s: ShippingSurchargeInfo,
): ApiShippingSurchargeInfo {
  return {
    ...s,
    rule: listingShippingSurchargeInfoRuleEnumToApiServerStoreListingShippingSurchargeInfoRuleEnumEnum(
      s.rule,
    ),
  };
}

function listingShippingSurchargeInfoRuleEnumToApiServerStoreListingShippingSurchargeInfoRuleEnumEnum(
  rule: ShippingSurchargeRule,
): ShippingSurchargeInfoRuleEnum {
  return rule as any;
}

export function listWeightToApiServerListWeight(
  weight: WeightInfo,
): SingleUnitWeightInfo | PoundWithOunceWeightInfo {
  if (weight.type === 'singleUnit') {
    return {
      value: weight.value,
      type: SingleUnitWeightInfoTypeEnum.SingleUnit,
      unit: listingSingleUnitWeightInfoUnitToApiServerStoreListingSingleUnitWeightInfoUnitEnum(
        weight.unit,
      ),
    };
  }
  return {
    type: PoundWithOunceWeightInfoTypeEnum.PoundWithOunce,
    lbValue: weight.lbValue,
    ozValue: weight.ozValue,
  };
}

export function listDimsToApiServerListDims(dims: DimsInfo): ApiDimsInfo {
  return {
    ...dims,
    unit: listingDistanceUnitToApiServerStoreListingDistanceUnitEnum(dims.unit),
  };
}

function listingSingleUnitWeightInfoUnitToApiServerStoreListingSingleUnitWeightInfoUnitEnum(
  unit: MassUnit,
): SingleUnitWeightInfoUnitEnum {
  return unit as any;
}

function listingDistanceUnitToApiServerStoreListingDistanceUnitEnum(
  unit: DistanceUnit,
): DimsInfoUnitEnum {
  return unit as any;
}

function listingOriginTypeToApiServerStoreListingOriginalInfoTypeEnum(
  type: ListingOriginType,
): StoreListingOriginalInfoTypeEnum {
  return type as any;
}

function listingOriginTypeToApiServerStoreListingVariantOriginalInfoTypeEnum(
  type: ListingOriginType,
): StoreListingVariantOriginalInfoTypeEnum {
  return type as any;
}

export function apiServerListingToListing(l: ProductsListing): Listing {
  return {
    ...l,
    type: l.type as any,
    discountStartDate: timestampFromDate(l.discountStartDate),
    discountEndDate: timestampFromDate(l.discountEndDate),
    moderationStatus: l.moderationStatus as any,
    moderatedAt: timestampFromDate(l.moderatedAt),
    dropshipDetails: l.dropshipDetails as any,
    dims: l.dims as any,
    weight: l.weight as any,
    shippingSurcharge: l.shippingSurcharge as any,
    createdAt:
      l.createdAt == null ? undefined : apiServerTimeToAppDate(l.createdAt),
    variants: l.variants?.map(apiServerStoreListingVariantToListingVariant),
    status:
      l.status == null
        ? undefined
        : apiServerProductsListingStatusEnum(l.status),
    rating: l.rating as any,
    originalInfo:
      l.originalInfo == null
        ? undefined
        : {
            ...l.originalInfo,
            type: apiServerStoreListingOriginalInfoTypeToListingOriginType(
              l.originalInfo.type,
            ),
          },
    meeting:
      l.meeting != null
        ? {
            ...l.meeting,
            startDate: timestampFromDate(l.meeting.startDate),
            endDate: timestampFromDate(l.meeting.endDate),
            repeatFrequency: type_erased(l.meeting.repeatFrequency),
            availableDates: l.meeting.availableDates?.map(date => Number(date)),
          }
        : undefined,
  };
}

export function apiServerMyStoreListingToListing(
  l: MyStoreListingItem,
): WithID<Listing> {
  return {
    ...l,
    type: l.type as any,
    discountStartDate: timestampFromDate(l.discountStartDate),
    discountEndDate: timestampFromDate(l.discountEndDate),
    moderationStatus: l.moderationStatus as any,
    moderatedAt: timestampFromDate(l.moderatedAt),
    rating: l.rating as any,
    dropshipDetails: l.dropshipDetails as any,
    dims: l.dims as any,
    weight: l.weight as any,
    shippingSurcharge: l.shippingSurcharge as any,
    createdAt:
      l.createdAt == null ? undefined : apiServerTimeToAppDate(l.createdAt),
    variants: l.variants?.map(apiServerStoreListingVariantToListingVariant),
    status:
      l.status == null
        ? undefined
        : apiServerMyStoreListingItemStatusEnum(l.status),
    originalInfo:
      l.originalInfo == null
        ? undefined
        : {
            ...l.originalInfo,
            type: apiServerStoreListingOriginalInfoTypeToListingOriginType(
              l.originalInfo.type,
            ),
          },
    meeting:
      l.meeting != null
        ? {
            ...l.meeting,
            startDate: timestampFromDate(l.meeting.startDate),
            endDate: timestampFromDate(l.meeting.endDate),
            repeatFrequency: type_erased(l.meeting.repeatFrequency),
            availableDates: l.meeting.availableDates?.map(date => Number(date)),
          }
        : undefined,
  };
}

export function apiServerStoreListingVariantToListingVariant(
  v: StoreListingVariant,
): ListingVariant {
  return {
    ...v,
    dims:
      v.dims == null
        ? undefined
        : {
            ...v.dims,
            unit: apiServerStoreListingDistanceUnitToListingDistanceUnitEnum(
              v.dims.unit,
            ),
          },
    weight:
      v.weight == null
        ? undefined
        : v.weight.type === 'singleUnit'
          ? {
              value: v.weight.value,
              type: 'singleUnit',
              unit: apiServerStoreListingSingleUnitWeightInfoUnitToListingSingleUnitWeightInfoUnitEnum(
                v.weight.unit,
              ),
            }
          : {
              type: 'poundWithOunce',
              lbValue: v.weight.lbValue,
              ozValue: v.weight.ozValue,
            },
    originalInfo:
      v.originalInfo == null
        ? undefined
        : {
            ...v.originalInfo,
            type: apiServerStoreListingOriginalInfoTypeToListingOriginType(
              v.originalInfo.type,
            ),
          },
  };
}

function apiServerStoreListingSingleUnitWeightInfoUnitToListingSingleUnitWeightInfoUnitEnum(
  unit: SingleUnitWeightInfoUnitEnum,
): MassUnit {
  return unit as any;
}

function apiServerStoreListingDistanceUnitToListingDistanceUnitEnum(
  unit: DimsInfoUnitEnum,
): DistanceUnit {
  return unit as any;
}

export function apiServerStoreListingOriginalInfoTypeToListingOriginType(
  t: StoreListingVariantOriginalInfoTypeEnum | StoreListingOriginalInfoTypeEnum,
): ListingOriginType {
  return t as any;
}

export function apiServerProductsListingStatusEnum(
  s: ProductsListingStatusEnum | ProductItemStatusEnum,
): ListingStatus {
  return s as any;
}

function apiServerMyStoreListingItemStatusEnum(
  s: MyStoreListingItemStatusEnum,
): ListingStatus {
  return s as any;
}

export function apiServerProductSearchItemToListing(
  l: ProductItem,
): WithID<Listing> {
  return {
    ...l,
    type: l.type as any,
    discountStartDate: timestampFromDate(l.discountStartDate),
    discountEndDate: timestampFromDate(l.discountEndDate),
    moderationStatus: l.moderationStatus as any,
    moderatedAt: timestampFromDate(l.moderatedAt),
    rating: l.rating as any,
    dropshipDetails: l.dropshipDetails as any,
    dims: l.dims as any,
    weight: l.weight as any,
    shippingSurcharge: l.shippingSurcharge as any,
    createdAt:
      l.createdAt == null ? undefined : apiServerTimeToAppDate(l.createdAt),
    variants: l.variants?.map(apiServerStoreListingVariantToListingVariant),
    status:
      l.status == null
        ? undefined
        : apiServerProductsListingStatusEnum(l.status),
    originalInfo:
      l.originalInfo == null
        ? undefined
        : {
            ...l.originalInfo,
            type: apiServerStoreListingOriginalInfoTypeToListingOriginType(
              l.originalInfo.type,
            ),
          },
    meeting:
      l.meeting != null
        ? {
            ...l.meeting,
            startDate: timestampFromDate(l.meeting.startDate),
            endDate: timestampFromDate(l.meeting.endDate),
            repeatFrequency: type_erased(l.meeting?.repeatFrequency),
            availableDates: l.meeting.availableDates?.map(date => Number(date)),
          }
        : undefined,
  };
}
